import React, { Fragment, useCallback, useState } from 'react';
import { ConfigProvider, Pagination } from 'antd';
import { CaretDownFilled, CaretRightFilled, CaretUpOutlined, CaretDownOutlined, MailOutlined } from '@ant-design/icons';
import { URL_CHANNEL, URL_NETWORK } from '../../../env';
import { ZNS } from '../../../components/ExportImgFigma';
import { CampaignStats, IReportCampaign, StatusDot, TotalRow } from '../../../api/reportCustomer/reportCustomerApi';
import { formatNumber } from '../../../services/mixin';
import { dataPagination, formatPrice } from '../../../until';
import viVN from 'antd/locale/vi_VN';
import { TypeTotalReport } from '.';

// ─── Types ────────────────────────────────────────────────────────────────────





// ─── Mock Data ────────────────────────────────────────────────────────────────

// const TOTAL_ROW: TotalRow = {
//     zns: { totalSend: 105000, successSend: 104500, sendRate: 95 },
//     sms: { totalSend: 5000, successSend: 5000, sendRate: 100 },
//     email: { totalSend: 2000, successSend: 2000, sendRate: 100 },
// };

// Trạng thái chiến dịch (0 = Chờ gửi, 1 = Đang gửi, 2 = Hoàn thành, 3 = Tạm dừng)
// ─── Default column widths ────────────────────────────────────────────────────

const DEFAULT_WIDTHS: Record<string, number> = {
    name: 230,
    trangThai: 110,
    mucTieu: 125,
    nhomKH: 185,
    khachHang: 130,
    zns_soGui: 130,
    zns_thanhCong: 125,
    zns_tyLe: 90,
    sms_soGui: 130,
    sms_thanhCong: 125,
    sms_tyLe: 90,
    email_soGui: 130,
    email_thanhCong: 125,
    email_tyLe: 90,
};

// ─── Status ───────────────────────────────────────────────────────────────────

export const STATUS_LIST = [
    { id: 0, title: 'Chờ gửi', color: '#FAAD14' },
    { id: 1, title: 'Đang gửi', color: '#1890FF' },
    { id: 2, title: 'Hoàn thành', color: '#52C41A' },
    { id: 3, title: 'Tạm dừng', color: '#EC2D30' },
];

const getStatus = (value?: number) =>
    STATUS_LIST.find(s => s.id === value);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n?: number) =>
    n == null || n === undefined ? '-' : n.toLocaleString('vi-VN');

const fmtStr = (s?: string | number) =>
    s == null || s === '' ? '-' : String(s);

// ─── Sub-components ───────────────────────────────────────────────────────────

const ResizeHandle: React.FC<{
    colKey: string;
    startWidth: number;
    onWidthChange: (key: string, w: number) => void;
}> = ({ colKey, startWidth, onWidthChange }) => {
    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const orig = startWidth;
            const onMove = (ev: MouseEvent) => {
                const next = Math.max(60, Math.min(600, orig + ev.clientX - startX));
                onWidthChange(colKey, next);
            };
            const onUp = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [colKey, startWidth, onWidthChange],
    );
    return <div className="ct-resize-handle" onMouseDown={onMouseDown} />;
};

const MucTieuBadge: React.FC<{ label: string; target: any }> = ({ label, target }) => (
    <span
        style={{
            border: `0.5px solid #${target?.colorBorder || '#d9d9d9'}`,
            backgroundColor: `#${target?.bgColor || '#d9d9d9'}`,
            color: `#${target?.colorText || '#000'}`,
            borderRadius: 8,
            padding: '2px 8px',
            fontSize: 12,
            fontWeight: 500,
            display: 'inline-block',
        }}
    >{label}</span>
);

const StatusDotEl: React.FC<{ dot: StatusDot; label: string }> = ({ dot, label }) => (
    <span className="ct-status"
    >
        {dot !== 'none' && <span className={`ct-status-dot ct-status-dot--${dot}`} />}
        {label || '-'}
    </span>
);

const IconPrefix: React.FC<{ icon: string }> = ({ icon }) => {
    return <img
        src={URL_CHANNEL + icon}
        onError={(e) => {
            e.currentTarget.src = ZNS;
        }}
    />;
};

type SortDir = 'asc' | 'desc' | null;
interface SortState { key: string; dir: SortDir }

const SortIcon: React.FC<{ colKey: string; sort: SortState }> = ({ colKey, sort }) => (
    <span className="ct-sort-icons">
        <CaretUpOutlined className={`ct-sort-up ${sort.key === colKey && sort.dir === 'asc' ? 'active' : ''}`} />
        <CaretDownOutlined className={`ct-sort-down ${sort.key === colKey && sort.dir === 'desc' ? 'active' : ''}`} />
    </span>
);

// ─── Main Component ───────────────────────────────────────────────────────────

interface CampaignTableProps {
    dataReportCampaign: IReportCampaign[];
    totalReport?: TypeTotalReport 
    pagination: {
        current: number;
        pageSize: number;
        total: number;
    };
    onPaginationChange: (page: number, pageSize: number) => void;
}
 
const CampaignTable: React.FC<CampaignTableProps> = ({
    dataReportCampaign,totalReport, pagination,  onPaginationChange
}) => {
    const [widths, setWidths] = useState<Record<string, number>>(DEFAULT_WIDTHS);
    const setW = useCallback((k: string, w: number) => setWidths(p => ({ ...p, [k]: w })), []);

    const [expanded, setExpanded] = useState<Record<string, boolean>>({
        p1: true,
        p2: true,
        p3: false,
        p4: false,
        p5: false,
        p6: false,
        p7: false,
        p8: false,
        p9: false,
        p10: false,
    });

    const [sort, setSort] = useState<SortState>({ key: '', dir: null });
   

    const toggleExpand = (id: string) =>
        setExpanded(p => ({ ...p, [id]: !p[id] }));

    const handleSort = (key: string) => {
        setSort(prev => {
            if (prev.key !== key) return { key, dir: 'asc' };
            if (prev.dir === 'asc') return { key, dir: 'desc' };
            if (prev.dir === 'desc') return { key: '', dir: null };
            return { key, dir: 'asc' };
        });
    };

    // Sort parent rows only
    const sortedData = React.useMemo(() => {
        if (!sort.key || !sort.dir) return dataReportCampaign;
        return [...dataReportCampaign].sort((a, b) => {
            let av: any, bv: any;
            switch (sort.key) {
                case 'name': av = a.title ?? a.name; bv = b.title ?? b.name; break;
                case 'trangThai': av = a.trangThai ?? a.status; bv = b.trangThai ?? b.status; break;
                case 'mucTieu': av = a.mucTieu ?? a.target?.title; bv = b.mucTieu ?? b.target?.title; break;
                case 'nhomKH': av = a.nhomKH ?? a.groupCustomer; bv = b.nhomKH ?? b.groupCustomer; break;
                case 'zns_soGui': av = a.zns?.totalSend ?? 0; bv = b.zns?.totalSend ?? 0; break;
                case 'zns_thanhCong': av = a.zns?.successSend ?? 0; bv = b.zns?.successSend ?? 0; break;
                case 'zns_tyLe': av = a.zns?.sendRate ?? 0; bv = b.zns?.sendRate ?? 0; break;
                case 'sms_soGui': av = a.sms?.totalSend ?? 0; bv = b.sms?.totalSend ?? 0; break;
                case 'sms_thanhCong': av = a.sms?.successSend ?? 0; bv = b.sms?.successSend ?? 0; break;
                case 'sms_tyLe': av = a.sms?.sendRate ?? 0; bv = b.sms?.sendRate ?? 0; break;
                case 'email_soGui': av = a.email?.totalSend ?? 0; bv = b.email?.totalSend ?? 0; break;
                case 'email_thanhCong': av = a.email?.successSend ?? 0; bv = b.email?.successSend ?? 0; break;
                case 'email_tyLe': av = a.email?.sendRate ?? 0; bv = b.email?.sendRate ?? 0; break;
                default: av = ''; bv = '';
            }
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [sort, dataReportCampaign]);

    // Pagination is handled server-side by parent, no local slicing needed
    const pagedData = sortedData;

    // Sortable header th helper
    const SortTh: React.FC<{
        colKey: string;
        children: React.ReactNode;
        rowSpan?: number;
        className?: string;
        style?: React.CSSProperties;
    }> = ({ colKey, children, rowSpan, className, style }) => (
        <th
            rowSpan={rowSpan}
            className={`ct-th-sortable ${className ?? ''}`}
            style={{ width: widths[colKey], ...style }}
            onClick={() => handleSort(colKey)}
        >
            <div className="ct-th-inner">
                <span className="ct-th-label">{children}</span>
                <SortIcon colKey={colKey} sort={sort} />
            </div>
            <ResizeHandle colKey={colKey} startWidth={widths[colKey]} onWidthChange={setW} />
        </th>
    );

    const PlainTh: React.FC<{
        colKey: string;
        children: React.ReactNode;
        rowSpan?: number;
        colSpan?: number;
        className?: string;
    }> = ({ colKey, children, rowSpan, colSpan, className }) => (
        <th
            rowSpan={rowSpan}
            colSpan={colSpan}
            className={`${className ?? ''}`}
            style={colSpan ? undefined : { width: widths[colKey] }}
        >
            {children}
            {!colSpan && <ResizeHandle colKey={colKey} startWidth={widths[colKey]} onWidthChange={setW} />}
        </th>
    );

    const statCells = (stats: CampaignStats) => (
        <>
            <td className="ct-td-num">{fmt(stats.totalSend)}</td>
            <td className="ct-td-num">{fmt(stats.successSend)}</td>
            <td className="ct-td-num">{stats.sendRate != null ? stats.sendRate : '-'}</td>
        </>
    );

    const emptyStatCells = () => (
        <>
            <td className="ct-td-num">-</td>
            <td className="ct-td-num">-</td>
            <td className="ct-td-num">-</td>
        </>
    );

    const renderStatCells = (stats?: CampaignStats) => {
        if (!stats || (stats.totalSend == null && stats.successSend == null && stats.sendRate == null)) return emptyStatCells();
        return statCells(stats);
    };

    return (
        <div className="ct-wrapper">
            <div className="ct-table-scroll">
                <table className="ct-table">
                    <colgroup>
                        <col style={{ width: widths.name }} />
                        <col style={{ width: widths.trangThai }} />
                        <col style={{ width: widths.mucTieu }} />
                        <col style={{ width: widths.nhomKH }} />
                        <col style={{ width: widths.khachHang }} />
                        {/* ZNS */}
                        <col style={{ width: widths.zns_soGui }} />
                        <col style={{ width: widths.zns_thanhCong }} />
                        <col style={{ width: widths.zns_tyLe }} />
                        {/* SMS */}
                        <col style={{ width: widths.sms_soGui }} />
                        <col style={{ width: widths.sms_thanhCong }} />
                        <col style={{ width: widths.sms_tyLe }} />
                        {/* Email */}
                        <col style={{ width: widths.email_soGui }} />
                        <col style={{ width: widths.email_thanhCong }} />
                        <col style={{ width: widths.email_tyLe }} />
                    </colgroup>

                    <thead>
                        {/* Row 1 — sticky via <tr> */}
                        <tr className="ct-thead-row1">
                            <SortTh colKey="name" className="ct-sticky-col ct-th-span">
                                Tên chiến dịch
                            </SortTh>
                            <SortTh colKey="trangThai" className="ct-th-span">Trạng thái</SortTh>
                            <SortTh colKey="mucTieu" className="ct-th-span">Mục tiêu</SortTh>
                            <SortTh colKey="nhomKH" className="ct-th-span">Nhóm khách hàng nhận</SortTh>
                            <PlainTh colKey="khachHang" className="ct-th-span">Khách hàng nhận</PlainTh>
                            <th colSpan={3} className="ct-group-header">ZNS</th>
                            <th colSpan={3} className="ct-group-header">SMS</th>
                            <th colSpan={3} className="ct-group-header ct-group-last">Email</th>
                        </tr>
                        {/* Row 2 — sticky via <tr> */}
                        <tr className="ct-thead-row2">
                            {/* sticky-left empty cells giữ chỗ cho basic cols → sub-cols không tràn vào */}
                            <th className="ct-sticky-col ct-th-empty" style={{ width: widths.name }} />
                            <th className="ct-th-empty" style={{ width: widths.trangThai }} />
                            <th className="ct-th-empty" style={{ width: widths.mucTieu }} />
                            <th className="ct-th-empty" style={{ width: widths.nhomKH }} />
                            <th className="ct-th-empty" style={{ width: widths.khachHang }} />
                            <SortTh colKey="zns_soGui">Số thông báo gửi</SortTh>
                            <SortTh colKey="zns_thanhCong">Gửi thành công</SortTh>
                            <SortTh colKey="zns_tyLe">Tỷ lệ (%)</SortTh>
                            <SortTh colKey="sms_soGui">Số thông báo gửi</SortTh>
                            <SortTh colKey="sms_thanhCong">Gửi thành công</SortTh>
                            <SortTh colKey="sms_tyLe">Tỷ lệ (%)</SortTh>
                            <SortTh colKey="email_soGui">Số thông báo gửi</SortTh>
                            <SortTh colKey="email_thanhCong">Gửi thành công</SortTh>
                            <SortTh colKey="email_tyLe">Tỷ lệ (%)</SortTh>
                        </tr>
                    </thead>

                    <tbody>
                        {/* TỔNG CỘNG */}
                        <tr className="ct-total-row">
                            <td className="ct-sticky-col ct-total-name">TỔNG CỘNG</td>
                            <td className="">-</td>
                            <td className="">-</td>
                            <td className="">-</td>
                            <td className="">-</td>
                            {renderStatCells(totalReport?.zns)}
                            {renderStatCells(totalReport?.sms)}
                            {renderStatCells(totalReport?.email)}
                        </tr>

                        {pagedData.map(row => (
                            <Fragment key={row.id}>
                                {/* Parent row */}
                                <tr className="ct-parent-row">
                                    <td
                                        className="ct-sticky-col ct-parent-name"
                                        onClick={() => row.children.length > 0 && toggleExpand(String(row.id))}
                                        style={{ cursor: row.children.length > 0 ? 'pointer' : 'default' }}
                                    >
                                        <div className="ct-tree-cell">
                                            {row.children.length > 0 ? (
                                                expanded[String(row.id)]
                                                    ? <CaretDownFilled className="ct-expand-icon" />
                                                    : <CaretRightFilled className="ct-expand-icon" />
                                            ) : (
                                                <span className="ct-expand-placeholder" />
                                            )}
                                            <span className="ct-name-text">{row.title ?? row.name}</span>
                                        </div>
                                    </td>
                                    <td className=" ct-td-status">
                                        {(() => {
                                            const s = getStatus(row.status);
                                            return s
                                                ? <div className="custom-page-css flex items-center gap-1">
                                                    <span
                                                        style={{
                                                            backgroundColor: `${s.color}`,
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            display: 'inline-block',

                                                        }}
                                                    />
                                                    <span  >{s.title}</span>
                                                </div>
                                                : '-';
                                        })()}
                                    </td>
                                    <td className="">
                                        {(row.target?.title)
                                            ? <MucTieuBadge label={row.target?.title ?? ''} target={row.target ?? 'khuyen-mai'} />
                                            : '-'}
                                    </td>
                                    <td className="ct-td-text">{fmtStr(row.groupCustomer)}</td>
                                    <td className="ct-td-num">{fmtStr(formatPrice(row.totalCustomerTarget))}</td>
                                    {renderStatCells(row.zns)}
                                    {renderStatCells(row.sms)}
                                    {renderStatCells(row.email)}
                                </tr>

                                {/* Child rows */}
                                {expanded[String(row.id)] && row.children.map(child => (
                                    <tr key={child.id} className="ct-child-row">
                                        <td className="ct-sticky-col ct-child-name">
                                            <div className="ct-tree-cell ct-child-cell">
                                                <span className="ct-expand-placeholder" />
                                                <IconPrefix icon={child.logo ?? ''} />
                                                <span className="ct-name-text">{child.templateTitle}</span>
                                            </div>
                                        </td>
                                        <td className="">
                                            <StatusDotEl dot={child.statusDot ?? 'none'} label={child.status ?? ''} />
                                        </td>
                                        <td className="">-</td>
                                        <td className="">-</td>
                                        <td className="">-</td>
                                        {renderStatCells(child.zns)}
                                        {renderStatCells(child.sms)}
                                        {renderStatCells(child.email)}
                                    </tr>
                                ))}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="ct-pagination">
                {/* <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    showSizeChanger
                    pageSizeOptions={['15', '25', '50', '100']}
                    showTotal={(total) => `Tổng cộng ${Math.ceil(total / pagination.pageSize)} trang`}
                    locale={{
                        items_per_page: '/ trang',
                        jump_to: 'Đến',
                        page: '',
                    }}

                    onChange={onPaginationChange}
                /> */}
                <ConfigProvider locale={viVN}>
                    <div className="pagination-block mt-auto">
                        <Pagination
                            showSizeChanger
                            current={pagination.current}
                            pageSize={pagination.pageSize}
                            total={pagination.total}
                            itemRender={(page, type, originalElement) => {
                                if (type === 'page') {
                                    return <span>{formatNumber(page)}</span>;
                                }
                                return originalElement;
                            }}
                            showTotal={() => `Tổng cộng ${dataPagination(pagination.total, pagination.pageSize, pagination.current).totalPage} trang`}
                            className='rs-pagination'
                            onChange={onPaginationChange}
                        />
                    </div>
                </ConfigProvider>
            </div>
        </div>
    );
};

export default CampaignTable;
